import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import {
  useIsCommunityAdmin,
  usePunditUserContext,
} from "@circle-react/contexts/punditUserContext";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useLayoutEffect, useRef } from "react";

import { useIsMobileShellEnabled } from "@/react/components/Mobile/hooks/useIsMobileShellEnabled";
import { useActivationLoadingScreenStore } from "@/react/components/NewVersionOptIn/ActivationLoadingScreen/activationLoadingScreenStore";

import { useIsCircleAiEnabled } from "../useCircleAiFeatureFlags";
import { useCircleAiStore } from "../useCircleAiStore";
import {
  AI_CONVERSATION_ID_PARAM,
  AI_INTENT_NEW,
  AI_PARAM,
  AI_VIEW_DEFAULT,
  VIEW_TYPE_PARAM,
  type CircleAiSearchParams,
  parseCircleAiUrlEntry,
  stripAiParams,
} from "./urlParams";

/**
 * Consumes the global CircleAI entry params on any route, then strips them.
 *
 * Mounted once in `CircleAIAppShell.Provider`, which is always alive — the
 * conversation panel unmounts while closed, so it cannot read a cold deep link.
 */
export const useCircleAiUrlEntry = (): void => {
  const isCircleAiEnabled = useIsCircleAiEnabled();
  const isAdmin = useIsCommunityAdmin();
  const isMobileShellEnabled = useIsMobileShellEnabled();
  const { isLoading: isPunditLoading } = usePunditUserContext();
  const search: CircleAiSearchParams = useSearch({ strict: false });
  const navigate = useNavigate();

  // Read live, not captured at mount: the Provider sits in the root route and
  // never remounts, so a client-side redirect into `?ai=…` (how /ai now
  // reaches the panel) would otherwise never be seen.
  const entry = parseCircleAiUrlEntry(search);
  const hasEntry = entry !== null;
  const modeParam = typeof search.mode === "string" ? search.mode : undefined;
  const conversationId = entry?.conversationId ?? null;
  const intent = entry?.intent ?? null;
  const view = entry?.view ?? AI_VIEW_DEFAULT;
  const hasConsumedRef = useRef(false);

  // useLayoutEffect, not useEffect: the width has to land before paint so a
  // deep link does not flash closed -> default -> fullscreen.
  useLayoutEffect(() => {
    // Params gone — either never present, or our own strip landed. Re-arm so a
    // later in-session deep link is picked up.
    if (!hasEntry) {
      hasConsumedRef.current = false;
      return;
    }
    if (hasConsumedRef.current) return;
    // `isAdmin` reads false while the payload is in flight, so waiting here is
    // what stops a legitimate admin being denied on a cold load.
    if (isPunditLoading) return;

    // Set before dispatching: the strip below is async, so this is what keeps
    // the re-renders in between (and Strict Mode's double invoke) idempotent.
    hasConsumedRef.current = true;

    // Denied access gets the params stripped and nothing else — no message,
    // matching what the old route did when it bounced them to the feed.
    const isAccessGranted = isAdmin && isCircleAiEnabled;

    // The V4 activation flow lands here via the /ai redirect; the screen stays
    // up across the hop. Cleared even when access is denied, so a member who
    // somehow reaches this URL is not left staring at the overlay.
    useActivationLoadingScreenStore.getState().clear();

    // A single navigation that both routes and drops the params, so it cannot
    // fight a separate strip over the same tick. `replace` is load-bearing:
    // the triggering URL would otherwise sit in history and re-fire this
    // effect on every back press.
    if (isAccessGranted && isMobileShellEnabled) {
      if (conversationId) {
        void navigate({
          to: "/ai/$conversationId",
          params: { conversationId },
          search: stripAiParams(search),
          replace: true,
        });
      } else {
        // `/ai` declares `mode` and the empty state reads it — carry it over.
        void navigate({
          to: "/ai",
          search: { mode: modeParam },
          replace: true,
        });
      }
      return;
    }

    if (isAccessGranted) {
      if (conversationId) {
        useCircleAiStore
          .getState()
          .openConversation(conversationId, null, null, { panelState: view });
      } else if (intent === AI_INTENT_NEW) {
        useCircleAiStore.getState().startNewChat({ panelState: view });
      } else {
        useCircleAIShellStore.getState().setConversationPanelState(view);
      }
    }

    void navigate({
      to: ".",
      search: prev => ({
        ...prev,
        [AI_PARAM]: undefined,
        [AI_CONVERSATION_ID_PARAM]: undefined,
        [VIEW_TYPE_PARAM]: undefined,
      }),
      replace: true,
      resetScroll: false,
    });
    // `search` is in the deps below: useSearch structurally shares its result,
    // so its identity only changes when the query string does.
  }, [
    conversationId,
    hasEntry,
    intent,
    isAdmin,
    isCircleAiEnabled,
    isMobileShellEnabled,
    isPunditLoading,
    modeParam,
    navigate,
    search,
    view,
  ]);
};
